import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { corridasApi } from '../../services/corridas-api';
import { instantesDelRango, type RangoDeFechas } from '../../lib/rango-de-fechas';
import type { Disparo, EventoDeLaLinea, ResumenDelPeriodo } from '../../types/corridas.types';

/** D-R2-14: mientras haya una en curso a la vista, se vuelve a pedir la primera página cada 5 s. */
export const ESPERA_MIENTRAS_CORRE_MS = 5000;

/**
 * Si la primera página nueva llega hasta el último evento de la primera vieja,
 * se puede poner en su lugar y conservar las que se trajeron con «Ver más» sin
 * hueco. Con una sola página no hay nada que conservar: se arranca de nuevo
 * (y se toma su cursor).
 */
function empalma(viejas: EventoDeLaLinea[][], nueva: EventoDeLaLinea[]): boolean {
  const ultimoViejo = viejas[0]?.[viejas[0].length - 1]?.id;
  return viejas.length > 1 && (!ultimoViejo || nueva.some((e) => e.id === ultimoViejo));
}

/**
 * La línea de tiempo con sus filtros, paginada («Ver más») y refrescándose sola
 * mientras corre algo. `version` sube cuando la pantalla sabe que hay algo nuevo
 * (después de «Revisar ahora»).
 */
export function useLineaDeTiempo(
  f: { estrategia: string | null; periodo: RangoDeFechas; disparos: Disparo[] },
  version = 0
) {
  const clave = JSON.stringify({ e: f.estrategia, p: f.periodo, d: [...f.disparos].sort() });
  const [paginas, setPaginas] = useState<EventoDeLaLinea[][]>([]);
  // Lo cargado a la vista, para que un refresco decida si empalma sin depender de `paginas`.
  const paginasALaVista = useRef(paginas);
  paginasALaVista.current = paginas;
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenDelPeriodo | null>(null);
  const [registroDesde, setRegistroDesde] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Ronda de arreglos: el error de «Ver más» va al pie; la lista que ya está, queda. */
  const [errorMas, setErrorMas] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  // Auditoría (menor): una respuesta que llega tarde, de un filtro que ya no está, se tira.
  const claveActual = useRef(clave);
  claveActual.current = clave;
  /**
   * Ronda de arreglos (revisión T10): cada vez que la lista arranca de nuevo desde
   * la primera página, la época sube. Un «Ver más» que se pidió en otra época
   * (con el cursor de la lista vieja) se tira: si no, dejaría huecos o repetidos.
   */
  const epoca = useRef(0);
  /** De qué filtro son los datos a la vista: un refresco del mismo filtro no los tapa. */
  const claveDeLosDatos = useRef<string | null>(null);

  const pedir = useCallback(
    (cursor: string | null) => {
      const q = JSON.parse(clave) as { e: string | null; p: RangoDeFechas; d: Disparo[] };
      return corridasApi.linea({
        ...instantesDelRango(q.p),
        estrategia: q.e,
        disparos: q.d,
        cursor,
      });
    },
    [clave]
  );

  useEffect(() => {
    let vigente = true;
    epoca.current++;
    // Ronda de arreglos: un refresco (la misma lista, `version` o «Reintentar»)
    // con datos a la vista no los tapa con esqueletos; un filtro nuevo, sí.
    const esRefresco = claveDeLosDatos.current === clave;
    if (!esRefresco) setCargando(true);
    setError(null);
    setErrorMas(null);
    pedir(null)
      .then((r) => {
        if (!vigente) return;
        claveDeLosDatos.current = clave;
        // Revisión final de rama (F4): un refresco no vuelve a la primera página
        // si empalma con lo cargado (lo mismo que el refresco de cada 5 s); el
        // cursor de «Ver más» sigue siendo el de la última página traída.
        const viejas = paginasALaVista.current;
        if (esRefresco && empalma(viejas, r.eventos)) {
          setPaginas([r.eventos, ...viejas.slice(1)]);
        } else {
          setPaginas([r.eventos]);
          setSiguiente(r.siguiente);
        }
        setResumen(r.resumen);
        setRegistroDesde(r.registroDesde);
      })
      .catch((e) => {
        if (!vigente) return;
        // Revisión final de rama (F4): si falla un refresco del mismo filtro, la
        // lista que ya está queda y el aviso va al pie (como el de «Ver más»).
        if (esRefresco) {
          setErrorMas(mensajeDeError(e, 'No se pudieron actualizar las corridas.'));
          return;
        }
        claveDeLosDatos.current = null;
        setError(mensajeDeError(e, 'No se pudieron traer las corridas.'));
      })
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [pedir, version, vuelta, clave]);

  const eventos = useMemo(() => {
    const vistos = new Set<string>();
    return paginas.flat().filter((e) => (vistos.has(e.id) ? false : (vistos.add(e.id), true)));
  }, [paginas]);
  const enCurso = eventos.some((e) => e.estado === 'EN_CURSO');

  // Mientras corre algo a la vista: la primera página otra vez, sin mostrar «cargando».
  const [tic, setTic] = useState(0);
  useEffect(() => {
    if (!enCurso) return;
    const deEsteFiltro = clave;
    const t = setTimeout(async () => {
      if (!document.hidden) {
        try {
          const r = await pedir(null);
          if (claveActual.current !== deEsteFiltro) return; // cambió el filtro mientras tanto
          // Sin hueco entre páginas: si la primera nueva ya no llega hasta el
          // último evento de la primera vieja (entraron más de 50), se arranca de
          // nuevo desde ésta y «Ver más» sigue desde su cursor. (El cálculo va
          // afuera del actualizador: un actualizador no llama a otro `set…`.)
          // Ronda de arreglos (revisión T10): con una sola página también se toma
          // el cursor nuevo; si no, «Ver más» seguía desde el último de la página
          // vieja y se salteaba los que el refresco empujó fuera de la primera.
          if (empalma(paginas, r.eventos)) {
            setPaginas((ps) => [r.eventos, ...ps.slice(1)]);
          } else {
            // Arranca de nuevo: un «Ver más» en vuelo es de la lista vieja.
            epoca.current++;
            setPaginas([r.eventos]);
            setSiguiente(r.siguiente);
          }
          if (r.resumen) setResumen(r.resumen);
        } catch {
          // El próximo intento.
        }
      }
      setTic((x) => x + 1);
    }, ESPERA_MIENTRAS_CORRE_MS);
    return () => clearTimeout(t);
  }, [enCurso, tic, pedir, clave, paginas]);

  const verMas = useCallback(async () => {
    if (!siguiente) return;
    setCargandoMas(true);
    setErrorMas(null);
    const deEsteFiltro = clave;
    const deEstaEpoca = epoca.current;
    const vigente = () => claveActual.current === deEsteFiltro && epoca.current === deEstaEpoca;
    try {
      const r = await pedir(siguiente);
      if (!vigente()) return;
      setPaginas((ps) => [...ps, r.eventos]);
      setSiguiente(r.siguiente);
    } catch (e) {
      if (vigente()) setErrorMas(mensajeDeError(e, 'No se pudieron traer más corridas.'));
    } finally {
      setCargandoMas(false);
    }
  }, [pedir, siguiente, clave]);

  return {
    eventos,
    resumen,
    registroDesde,
    cargando,
    cargandoMas,
    error,
    errorMas,
    haySiguiente: siguiente != null,
    verMas,
    reintentar: () => setVuelta((x) => x + 1),
  };
}
